import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { MAIN_SITE_URL } from "@/utils/constants";
import { generateArticleJsonLd, generateBreadcrumbJsonLd, generateFaqJsonLd } from "@/utils/seo-schema";

const articlePath = "/artykul/kakobuy-shipping-polska";
const articleImage = "https://readdy.ai/api/search-image?query=Cardboard%20shipping%20box%20on%20warm%20cream%20desk%20surface%20next%20to%20a%20laptop%20showing%20delivery%20tracking%20map%2C%20soft%20natural%20morning%20light%20from%20window%2C%20minimalist%20ecommerce%20logistics%20photography%2C%20warm%20beige%20and%20sand%20tones%2C%20clean%20modern%20aesthetic%2C%20international%20shipping%20concept%2C%20calm%20organized%20workspace%2C%20editorial%20style&width=1200&height=800&seq=kakobuy-article-shipping&orientation=landscape";

const schemas = [
  generateArticleJsonLd({
    headline: "Kakobuy Shipping Polska — Wysyłka, koszty i czas dostawy do Polski",
    description: "Kompletny przewodnik po wysyłce zamówień z Kakobuy do Polski. Sprawdź dostępne linie wysyłkowe, porównaj koszty i dowiedz się jak uniknąć problemów z cłem.",
    datePublished: "2026-07-01",
    dateModified: "2026-07-21",
    imageUrl: articleImage,
    articlePath,
  }),
  generateBreadcrumbJsonLd([
    { name: "Strona główna", path: "/" },
    { name: "Kakobuy Shipping Polska", path: articlePath },
  ]),
  generateFaqJsonLd([
    { question: "Ile trwa wysyłka Kakobuy do Polski?", answer: "Standardowy czas dostawy wynosi od 10 do 25 dni roboczych. Szybsze opcje (DHL, FedEx) mogą dostarczyć paczkę w 5–10 dni." },
    { question: "Czy muszę płacić cło?", answer: "VAT jest naliczany od wszystkich przesyłek spoza UE. Dodatkowe cło może być naliczone przy zamówieniach powyżej 150 euro." },
    { question: "Czy mogę połączyć kilka zamówień w jedną paczkę?", answer: "Tak, Kakobuy oferuje usługę konsolidacji — możesz połączyć produkty od różnych sprzedawców w jedną przesyłkę, co obniża koszty wysyłki." },
  ]),
];

export default function KakobuyShippingPolskaArticle() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-600 mb-8 flex-wrap">
              <Link to="/" className="hover:text-foreground-900 transition-colors cursor-pointer">Strona główna</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
              <span className="text-foreground-900 font-medium">Kakobuy Shipping Polska</span>
            </nav>

            <header className="mb-10">
              <div className="inline-flex items-center gap-2 bg-primary-100 px-4 py-1.5 rounded-full mb-4">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-ship-line text-primary-500 text-sm"></i></span>
                <span className="text-xs font-semibold text-primary-700 whitespace-nowrap">Dostawa</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                Kakobuy Shipping Polska — Wysyłka, koszty i czas dostawy do Polski
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Kompletny przewodnik po wysyłce zamówień z Kakobuy do Polski. Sprawdź dostępne linie wysyłkowe, porównaj koszty i dowiedz się jak uniknąć problemów z cłem.
              </p>
              <div className="flex items-center gap-3 text-xs text-foreground-600 flex-wrap">
                <span>Ostatnia aktualizacja: Lipiec 2026</span>
                <span className="text-foreground-400">•</span>
                <span>Czas czytania: 11 minut</span>
              </div>
            </header>

            <div className="w-full h-56 md:h-72 rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Cardboard%20shipping%20box%20on%20warm%20cream%20desk%20surface%20next%20to%20a%20laptop%20showing%20delivery%20tracking%20map%2C%20soft%20natural%20morning%20light%20from%20window%2C%20minimalist%20ecommerce%20logistics%20photography%2C%20warm%20beige%20and%20sand%20tones%2C%20clean%20modern%20aesthetic%2C%20international%20shipping%20concept%2C%20calm%20organized%20workspace%2C%20editorial%20style&width=1200&height=800&seq=kakobuy-article-shipping&orientation=landscape"
                alt="Kakobuy Shipping Polska — Wysyłka do Polski"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-8">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Jak działa wysyłka Kakobuy do Polski?</h2>
                <p className="mb-3">Zamawianie produktów z Kakobuy do Polski jest prostsze niż mogłoby się wydawać. Platforma oferuje kilka linii wysyłkowych, które różnią się ceną, czasem dostawy i poziomem oferowanych usług dodatkowych.</p>
                <p className="mb-3">Proces wygląda następująco: po dokonaniu zakupu produkty trafiają do magazynu Kakobuy, gdzie są sprawdzane i fotografowane (zdjęcia QC). Następnie możesz wybrać preferowaną linię wysyłkową i opłacić przesyłkę. Paczka jest konsolidowana i wysyłana pod wskazany adres w Polsce.</p>
                <p>Średni czas dostawy do Polski wynosi od 10 do 25 dni roboczych, w zależności od wybranej linii wysyłkowej i aktualnego obciążenia logistycznego.</p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Dostępne linie wysyłkowe — porównanie</h2>
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200/70">
                        <th className="text-left py-2 pr-4 text-xs font-semibold text-foreground-700 whitespace-nowrap">Linia wysyłkowa</th>
                        <th className="text-left py-2 pr-4 text-xs font-semibold text-foreground-700 whitespace-nowrap">Czas dostawy</th>
                        <th className="text-left py-2 pr-4 text-xs font-semibold text-foreground-700 whitespace-nowrap">Koszt (szac.)</th>
                        <th className="text-left py-2 text-xs font-semibold text-foreground-700 whitespace-nowrap">Tracking</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["DHL / FedEx", "5–10 dni", "Wyższy", "Pełny tracking"],
                        ["Linia lotnicza EU", "10–15 dni", "Średni", "Tracking do drzwi"],
                        ["Linia ekonomiczna", "15–25 dni", "Niższy", "Podstawowy tracking"],
                        ["China Post / Poczta", "20–40 dni", "Najniższy", "Ograniczony"],
                      ].map((row, i) => (
                        <tr key={i} className="border-b border-background-200/70 last:border-0">
                          <td className="py-2.5 pr-4 text-foreground-800 font-medium whitespace-nowrap">{row[0]}</td>
                          <td className="py-2.5 pr-4 text-foreground-600 whitespace-nowrap">{row[1]}</td>
                          <td className="py-2.5 pr-4 text-foreground-600 whitespace-nowrap">{row[2]}</td>
                          <td className="py-2.5 text-foreground-600 whitespace-nowrap">{row[3]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Cło i opłaty celne przy zamówieniach do Polski</h2>
                <p className="mb-3">Przy zamówieniach spoza Unii Europejskiej należy liczyć się z możliwością naliczenia opłat celnych. Od 1 lipca 2021 roku wszystkie przesyłki spoza UE podlegają opodatkowaniu VAT, niezależnie od wartości.</p>
                <p className="mb-3">Dla zamówień o wartości powyżej 150 euro mogą zostać naliczone dodatkowe opłaty celne. Warto uwzględnić ten koszt w swoim budżecie zakupowym.</p>
                <div className="bg-accent-100/50 border border-accent-200/60 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center"><i className="ri-information-line text-accent-500"></i></span>
                    Wskazówka praktyczna
                  </h3>
                  <p className="text-xs text-foreground-700">Niektórzy sprzedawcy oferują wysyłkę z magazynów europejskich — wtedy unikasz opłat celnych. W arkuszu Kakobuy Spreadsheet produkty z magazynów EU są często oznaczone.</p>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Jak śledzić przesyłkę Kakobuy?</h2>
                <p className="mb-3">Po nadaniu przesyłki otrzymasz numer śledzenia (tracking number), który pozwala na bieżąco monitorować status dostawy. Numer śledzenia znajdziesz w panelu użytkownika Kakobuy w sekcji zamówień.</p>
                <p className="mb-3">Większość linii wysyłkowych oferuje śledzenie online — wystarczy wpisać numer na stronie przewoźnika. Do popularnych narzędzi do śledzenia przesyłek międzynarodowych należą uniwersalne trackery jak 17track czy ParcelsApp.</p>
                <p>W przypadku opóźnień warto skontaktować się z obsługą klienta Kakobuy, która może pomóc w wyjaśnieniu sytuacji i przyspieszeniu dostawy.</p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">FAQ — Wysyłka Kakobuy do Polski</h2>
                <div className="space-y-3">
                  {[
                    { q: "Ile trwa wysyłka Kakobuy do Polski?", a: "Standardowy czas dostawy wynosi od 10 do 25 dni roboczych. Szybsze opcje (DHL, FedEx) mogą dostarczyć paczkę w 5–10 dni." },
                    { q: "Czy muszę płacić cło?", a: "VAT jest naliczany od wszystkich przesyłek spoza UE. Dodatkowe cło może być naliczone przy zamówieniach powyżej 150 euro." },
                    { q: "Czy mogę połączyć kilka zamówień w jedną paczkę?", a: "Tak, Kakobuy oferuje usługę konsolidacji — możesz połączyć produkty od różnych sprzedawców w jedną przesyłkę, co obniża koszty wysyłki." },
                  ].map((faq, idx) => (
                    <details key={idx} className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden cursor-pointer">
                      <summary className="flex items-center justify-between p-4 text-sm font-semibold text-foreground-900 list-none">
                        {faq.q}
                        <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180"><i className="ri-arrow-down-s-line"></i></span>
                      </summary>
                      <div className="px-4 pb-4 text-sm text-foreground-700 leading-relaxed">{faq.a}</div>
                    </details>
                  ))}
                </div>
              </section>

              <section className="bg-gradient-to-r from-accent-100/60 to-primary-100/60 rounded-2xl p-6 md:p-8 text-center border border-accent-200/40">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">Znajdź produkty z szybką wysyłką do Polski</h2>
                <p className="text-sm text-foreground-700 mb-5 max-w-md mx-auto">Skorzystaj z Kakobuy Spreadsheet, aby szybko znaleźć produkty z oznaczonymi opcjami wysyłki.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto bg-accent-500 text-background-50 font-semibold px-8 py-3 rounded-full hover:bg-accent-600 transition-all text-sm whitespace-nowrap cursor-pointer">Otwórz Kakobuy Spreadsheet</a>
                  <Link to="/" className="w-full sm:w-auto bg-background-50 text-foreground-900 font-semibold px-8 py-3 rounded-full border border-background-300/60 hover:bg-background-100 transition-all text-sm whitespace-nowrap cursor-pointer">Powrót na stronę główną</Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: s }} />
      ))}
    </div>
  );
}